/**
 * Centralized API request wrappers.
 *
 * Every frontend API call should go through these helpers instead of
 * importing axios directly. They prepend API_BASE and inherit the
 * global axios interceptors set up in AuthContext (auth headers,
 * effective-role header, token refresh, etc.).
 *
 * Usage:
 *   import { apiGet, apiPost } from "../lib/api-client";
 *   const res = await apiGet("/athlete/profile");
 *   const res = await apiPost("/invites", { email });
 *
 * Config passthrough — any axios request config (signal, timeout,
 * params, headers overrides) can be passed as the last argument:
 *   apiGet("/search", { params: { q: "MIT" }, signal: controller.signal })
 *   apiPost("/ai/briefing", {}, { timeout: 50000 })
 */
import axios from "axios";
import { API_BASE } from "./api";

/* ── Optional debug logging (disabled by default) ─────────────────
 * Set  localStorage.setItem("api_debug", "1")  in the browser
 * console to enable request/response logging during development.
 * Remove the key (or set "0") to silence it again.
 * No production impact — the check short-circuits immediately.
 */
const __debug = () => localStorage.getItem("api_debug") === "1";

function logDebug(method, path, extra) {
  if (!__debug()) return;
  // eslint-disable-next-line no-console
  console.groupCollapsed(`[api] ${method.toUpperCase()} ${path}`);
  if (extra) console.log(extra); // eslint-disable-line no-console
  console.groupEnd(); // eslint-disable-line no-console
}

/** GET request — `config` supports params, signal, headers, etc. */
export function apiGet(path, config) {
  logDebug("get", path, config);
  return axios.get(`${API_BASE}${path}`, config);
}

/** POST request — `data` is the JSON body, `config` is optional. */
export function apiPost(path, data, config) {
  logDebug("post", path, { data, config });
  return axios.post(`${API_BASE}${path}`, data, config);
}

/** PUT request — full-replace semantics. */
export function apiPut(path, data, config) {
  logDebug("put", path, { data, config });
  return axios.put(`${API_BASE}${path}`, data, config);
}

/** PATCH request — partial-update semantics. */
export function apiPatch(path, data, config) {
  logDebug("patch", path, { data, config });
  return axios.patch(`${API_BASE}${path}`, data, config);
}

/** DELETE request — `config` can carry { data } for request bodies. */
export function apiDelete(path, config) {
  logDebug("delete", path, config);
  return axios.delete(`${API_BASE}${path}`, config);
}
